import { MAX_INPUT_LENGTH } from 'Pages/TeamDashboard/CreateReview/components/constants';

import http from 'utlis/http';

const designAuditApi = process.env.REACT_APP_DESIGN_AUDIT_BASE_URL;

/**
 * Infer product context from design and analytics screenshots.
 *
 * Sends design images either as multipart file uploads (fetched from their S3 URLs)
 * or as plain URL strings via frame_url_* (backend fetches from S3 server-side,
 * avoiding browser CORS restrictions for library images).
 *
 * Request: multipart/form-data
 *   - frame_image_0, frame_image_1, ... — design images as files (when useFrameUrls=false)
 *   - frame_url_0, frame_url_1, ...     — design image URLs; backend downloads them (when useFrameUrls=true)
 *   - analytics_image_urls              — JSON array string of analytics URLs (optional)
 *   - max_chars                         — max character length for generated fields
 *
 * Response: { title, audience, problem, business_goal }
 */
export const getAiContextSuggestions = async (
  imageUrls,
  analyticsImageUrls = null,
  signal = null,
  useFrameUrls = false,
  lens = null
) => {
  if (!imageUrls || imageUrls.length === 0) {
    throw new Error('At least one design image URL is required');
  }

  const formData = new FormData();

  if (useFrameUrls) {
    // Pass URLs as strings — backend downloads from S3 (avoids browser CORS on library images)
    imageUrls.forEach((url, index) => {
      formData.append(`frame_url_${index}`, url);
    });
  }
  else {
    // Fetch each design image from its S3 URL, collect in order, then append
    const imageFiles = await Promise.all(
      imageUrls.map(async (url, index) => {
        const response = await fetch(url, signal ? { signal } : undefined);
        const blob = await response.blob();
        const ext = blob.type ? blob.type.split('/')[1] : 'png';

        return new File([blob], `design_${index + 1}.${ext}`, { type: blob.type || 'image/png' });
      })
    );

    imageFiles.forEach((file, index) => {
      formData.append(`frame_image_${index}`, file);
    });
  }

  // Pass analytics URLs as a JSON array string when provided
  const analyticsUrls = Array.isArray(analyticsImageUrls) && analyticsImageUrls.length > 0
    ? analyticsImageUrls
    : null;

  if (analyticsUrls && analyticsUrls.length > 0) {
    formData.append('analytics_image_urls', JSON.stringify(analyticsUrls));
  }

  formData.append('max_chars', MAX_INPUT_LENGTH - 20);

  if (lens) {
    formData.append('lens', lens);
  }

  const { data } = await http.post(
    `${designAuditApi}/infer_context`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-BACKEND-URL': process.env.REACT_APP_X_BACKEND_URL,
      },
      ...(signal ? { signal } : {}),
    }
  );

  const context = data.context || {};

  return {
    title: context.title || '',
    audience: context.target_audience || '',
    problem: context.problem_statement || '',
    businessGoal: context.business_goal || '',
    productName: context.product_name || '',
  };
};

/**
 * Enrich existing context with analytics insights.
 *
 * Takes the context returned by infer_context and adds analytics_image_urls,
 * returns an updated context that includes observed_analytics.
 *
 * Request: application/json
 *   - context: { title, target_audience, problem_statement, business_goal }
 *   - analytics_image_urls: string[]
 *   - max_chars: number
 *
 * Response: { status, context: { ...existing fields, observed_analytics } }
 */
export const getAiContextWithAnalytics = async (
  existingContext,
  analyticsImageUrls,
  signal = null
) => {
  if (!analyticsImageUrls || analyticsImageUrls.length === 0) {
    throw new Error('At least one analytics image URL is required');
  }

  const formData = new FormData();

  formData.append('analytics_image_urls', JSON.stringify(analyticsImageUrls));
  formData.append('max_chars', MAX_INPUT_LENGTH - 20);
  formData.append('context', JSON.stringify({
    title: existingContext.title || '',
    target_audience: existingContext.audience || '',
    problem_statement: existingContext.problem || '',
    business_goal: existingContext.businessGoal || '',
  }));

  const { data } = await http.post(
    `${designAuditApi}/infer_context_with_analytics`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-BACKEND-URL': process.env.REACT_APP_X_BACKEND_URL,
      },
      ...(signal ? { signal } : {}),
    }
  );

  const context = data.context || {};

  return {
    title: context.title || '',
    audience: context.target_audience || '',
    problem: context.problem_statement || '',
    businessGoal: context.business_goal || '',
    observedAnalytics: context.observed_analytics || '',
  };
};
